import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Phone, 
  Clock,
  Shield,
  CheckCircle2,
  Users,
  Plane,
  MapPin,
  Car,
  Star,
  Award,
  Headphones,
  ArrowRight,
  Sparkles,
  Heart,
  Zap,
  ThumbsUp,
  Hotel,
  HelpCircle,
  MessageCircle,
  DollarSign,
  Compass,
  Building2
} from 'lucide-react';
import { TransferBookingForm } from '@/components/TransferBookingForm';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useTours } from '@/hooks/useTours';
import { TourCard } from '@/components/TourCard';
import Layout from '@/components/Layout';
import { useHeroSlides } from '@/hooks/useHeroSlides';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getIcon } from '@/lib/iconMapper';

const HomePage = () => {
  const { testimonials } = useTestimonials();
  const { tours } = useTours();
  const { data: heroSlides, isLoading: slidesLoading } = useHeroSlides();
  const { getConfig } = useSiteConfig();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const featuredTestimonials = testimonials.filter(t => t.is_featured).slice(0, 3);
  const featuredTours = tours.filter(t => t.is_featured).slice(0, 4);
  const allActiveTours = tours.filter(t => t.is_active);
  
  const phoneNumber = getConfig('contact_phone')?.phone || '+1 (809) 555-0123';

  useEffect(() => {
    if (heroSlides && heroSlides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [heroSlides]);

  const nextSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }
  };

  const prevSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
    }
  };

  // Get CMS content with fallbacks
  const servicesData = getConfig('home_services_items', [
    { icon: "Plane", title: "Airport Transfers", description: "Reliable pickup and drop-off at all Dominican Republic airports", color: "primary", link: "/transfers" },
    { icon: "Building2", title: "Hotel Transfers", description: "Comfortable transportation between hotels and resorts", color: "secondary", link: "/transfers" },
    { icon: "Compass", title: "Excursions & Tours", description: "Discover the best tours and adventures across the island", color: "accent", link: "/tours" },
    { icon: "Car", title: "Private Tours", description: "Customized private transportation for your perfect trip", color: "tropical", link: "/tours" }
  ]);

  const featuresData = getConfig('home_why_items', [
    { icon: "Shield", title: "Safe & Secure", description: "Licensed, insured, and safety-certified vehicles" },
    { icon: "Clock", title: "24/7 Service", description: "Round-the-clock support for all your needs" },
    { icon: "DollarSign", title: "Best Prices", description: "Competitive rates with no hidden fees" },
    { icon: "Star", title: "Top Rated", description: "Consistently rated 5 stars by travelers" }
  ]);

  const statsData = getConfig('home_stats', [
    { value: "10K+", label: "Happy Travelers", icon: "Users" },
    { value: "500+", label: "Tours Completed", icon: "MapPin" },
    { value: "15+", label: "Years Experience", icon: "Award" },
    { value: "4.9", label: "Average Rating", icon: "Star" }
  ]);

  const faqsData = getConfig('home_faq', [
    { question: "How do I book a transfer or tour?", answer: "You can easily book through our website by selecting your desired service, filling out the booking form, and completing the secure payment. You will receive an instant confirmation email with all the details." },
    { question: "What is included in airport transfers?", answer: "Our airport transfers include meet and greet service, flight monitoring, comfortable air-conditioned vehicles, professional English-speaking drivers, bottled water, and door-to-door service to your hotel or destination." },
    { question: "Do you offer private tours?", answer: "Yes! We offer fully customizable private tours where you can choose your destinations, timing, and activities. Contact us to create your perfect Dominican Republic adventure." },
    { question: "What happens if my flight is delayed?", answer: "We monitor all flight arrivals in real-time. If your flight is delayed, your driver will adjust the pickup time accordingly at no extra charge. We are always there when you land." },
    { question: "Can I cancel or modify my booking?", answer: "Yes, you can cancel or modify your booking up to 24 hours before the scheduled service for a full refund. Changes made within 24 hours may be subject to availability." },
    { question: "What payment methods do you accept?", answer: "We accept all major credit cards, PayPal, and cash payments. Full payment is required at the time of booking to confirm your reservation." },
    { question: "Are child seats available?", answer: "Yes, we provide child seats and booster seats upon request at no additional charge. Please specify your requirements when making your booking." },
    { question: "Do your drivers speak English?", answer: "Yes, all our drivers are fluent in English and Spanish. Many also speak other languages including French, German, and Italian to better serve our international guests." }
  ]);

  // CMS text content
  const toursBadge = getConfig('home_tours_badge', 'Explore Paradise');
  const toursTitle = getConfig('home_tours_title', 'Our Tours & Excursions');
  const toursDescription = getConfig('home_tours_description', 'Discover the best adventures the Dominican Republic has to offer. From pristine beaches to hidden waterfalls, ancient caves to vibrant culture.');
  const whyBadge = getConfig('home_why_badge', 'Why Choose Us');
  const whyTitle = getConfig('home_why_title', 'Your Trusted Travel Partner');
  const whyDescription = getConfig('home_why_description', 'With over 15 years of experience, we have perfected the art of Caribbean hospitality');
  const ctaTitle = getConfig('home_cta_title', 'Ready to Explore Paradise?');
  const ctaDescription = getConfig('home_cta_description', 'Book your transfer or tour today and experience the Caribbean with comfort and style');

  if (slidesLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded-full animate-bounce" />
            <div className="w-3 h-3 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="w-3 h-3 bg-tropical rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Servicio Turistico Rachel | Premium Transfers & Tours in Dominican Republic</title>
        <meta name="description" content="Experience the best transportation and tour services in Dominican Republic. Airport transfers, hotel pickups, and unforgettable excursions." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section with Dynamic Slider */}
        <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background Decorations */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-tropical/20 rounded-full blur-3xl animate-blob z-0" />
          <div className="absolute bottom-40 right-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl animate-blob z-0" style={{ animationDelay: '-2s' }} />
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-palm/10 rounded-full blur-2xl animate-float z-0" />
          
          {/* Background Slides */}
          {heroSlides?.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-all duration-1000 ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image_url})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-primary/50" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
            </div>
          ))}
          
          {/* Navigation Arrows */}
          {heroSlides && heroSlides.length > 1 && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 md:p-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 border border-white/20"
              >
                <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 md:p-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 border border-white/20"
              >
                <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
              </button>
            </>
          )}

          {/* Slide Indicators */}
          {heroSlides && heroSlides.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`rounded-full transition-all duration-500 ${
                    index === currentSlide 
                      ? 'w-8 h-3 bg-secondary' 
                      : 'w-3 h-3 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}
          
          <div className="relative z-10 container mx-auto px-4 py-20">
            <div className="text-center text-white mb-12">
              <Badge className="mb-6 bg-secondary/90 hover:bg-secondary text-white border-0 backdrop-blur-sm text-base md:text-lg px-6 md:px-8 py-2 md:py-3 shadow-xl font-bold animate-fade-in-down">
                <Sparkles className="w-4 h-4 mr-2" />
                Premium Transfer & Tour Service
              </Badge>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in">
                Your Premier Transfer
                <br />
                <span className="bg-gradient-to-r from-secondary via-tropical to-secondary bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto]">
                  & Tour Service
                </span>
                <br />
                <span className="text-white/90">in Punta Cana</span>
              </h1>
              
              <p className="text-lg md:text-xl lg:text-2xl mb-8 text-white/80 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Experience the Caribbean with Comfort, Safety & Style
              </p>
            </div>

            {/* Transfer Booking Form */}
            <div className="max-w-5xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <TransferBookingForm />
            </div>
          </div>
          
          {/* Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 50C360 100 720 0 1080 50C1260 75 1380 62 1440 50V100H0V50Z" fill="hsl(var(--background))" />
            </svg>
          </div>
        </section>

        {/* All Tours Section - Right Below Hero */}
        {allActiveTours.length > 0 && (
          <section className="py-20 md:py-24 bg-muted/30 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-20 right-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-20 left-10 w-32 h-32 bg-tropical/5 rounded-full blur-3xl" />
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 md:mb-12 gap-4">
                <div className="animate-fade-in-left">
                  <Badge className="mb-4 bg-tropical/10 text-tropical border-tropical/20 px-4 py-1">
                    <MapPin className="w-4 h-4 mr-2" />
                    {toursBadge}
                  </Badge>
                  <h2 className="text-3xl md:text-5xl font-bold">
                    Our <span className="gradient-text">Tours & Excursions</span>
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg mt-4 max-w-2xl">
                    {toursDescription}
                  </p>
                </div>
                <Link to="/tours" className="animate-fade-in-right">
                  <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 shadow-lg group">
                    View All Tours
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                {allActiveTours.map((tour, index) => (
                  <div 
                    key={tour.id} 
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <TourCard tour={tour} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Stats Section */}
        <section className="relative py-16 bg-gradient-to-r from-primary via-primary/95 to-accent overflow-hidden">
          {/* Animated Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.4%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')]" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {statsData.map((stat: any, index: number) => (
                <div 
                  key={stat.label}
                  className="text-center animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="text-3xl md:text-5xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-white/80 text-sm md:text-base font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 md:py-24 relative overflow-hidden">
          {/* Background Mesh */}
          <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12 md:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 px-4 py-1 animate-fade-in">
                <Sparkles className="w-4 h-4 mr-2" />
                Our Services
              </Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="gradient-text">Premium Transportation</span>
                <br />
                <span className="text-foreground">Services</span>
              </h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                From airport pickups to unforgettable tours, we provide reliable and comfortable transportation across the Dominican Republic
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {servicesData.map((service: any, index: number) => {
                const IconComponent = getIcon(service.icon);
                return (
                  <Link to={service.link} key={service.title}>
                    <Card 
                      className="group relative overflow-hidden border-0 shadow-lg hover-lift cursor-pointer h-full animate-fade-in-up bg-card/80 backdrop-blur-sm"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {/* Gradient overlay on hover */}
                      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                        service.color === 'primary' ? 'bg-gradient-to-br from-primary/5 to-primary/10' :
                        service.color === 'secondary' ? 'bg-gradient-to-br from-secondary/5 to-secondary/10' :
                        service.color === 'accent' ? 'bg-gradient-to-br from-accent/5 to-accent/10' :
                        'bg-gradient-to-br from-tropical/5 to-tropical/10'
                      }`} />
                      
                      <CardContent className="p-6 md:p-8 relative z-10">
                        <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-5 md:mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${
                          service.color === 'primary' ? 'bg-primary/10 text-primary' :
                          service.color === 'secondary' ? 'bg-secondary/10 text-secondary' :
                          service.color === 'accent' ? 'bg-accent/10 text-accent' :
                          'bg-tropical/10 text-tropical'
                        }`}>
                          <IconComponent className="w-7 h-7 md:w-8 md:h-8" />
                        </div>
                        
                        <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 group-hover:text-primary transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-muted-foreground text-sm md:text-base mb-4">
                          {service.description}
                        </p>
                        
                        <div className="flex items-center text-primary font-semibold group-hover:gap-3 gap-2 transition-all text-sm md:text-base">
                          Learn More
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Tours Section */}
        {featuredTours.length > 0 && (
          <section className="py-20 md:py-24 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 md:mb-12 gap-4">
                <div className="animate-fade-in-left">
                  <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 px-4 py-1">
                    <Heart className="w-4 h-4 mr-2" />
                    Featured Tours
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Unforgettable <span className="gradient-text-sunset">Adventures</span>
                  </h2>
                </div>
                <Link to="/tours" className="animate-fade-in-right">
                  <Button variant="outline" className="group border-2">
                    View All Tours
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {featuredTours.map((tour, index) => (
                  <div 
                    key={tour.id} 
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <TourCard tour={tour} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Why Choose Us Section */}
        <section className="py-20 md:py-24 relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-tropical/5 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
              <Badge className="mb-4 bg-palm/10 text-palm border-palm/20 px-4 py-1 animate-fade-in">
                  <Zap className="w-4 h-4 mr-2" />
                  {whyBadge}
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  {whyTitle} in <span className="gradient-text">Paradise</span>
                </h2>
                <p className="text-muted-foreground text-base md:text-lg mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  {whyDescription}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                  {featuresData.map((feature: any, index: number) => {
                    const IconComponent = getIcon(feature.icon);
                    return (
                      <div 
                        key={feature.title}
                        className="flex items-start gap-4 animate-fade-in"
                        style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                      >
                        <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-1">{feature.title}</h4>
                          <p className="text-muted-foreground text-sm">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-8 md:mt-10 flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.7s' }}>
                  <Link to="/transfers">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 shadow-primary group">
                      Book Transfer
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/about">
                    <Button size="lg" variant="outline" className="border-2">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="relative order-1 lg:order-2 animate-fade-in-right">
                {/* Image grid */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div className="space-y-3 md:space-y-4">
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[3/4] group">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Plane className="w-12 h-12 text-white/80" />
                      </div>
                    </div>
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square group">
                      <div className="absolute inset-0 bg-gradient-to-br from-tropical to-secondary" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <MapPin className="w-10 h-10 text-white/80" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 md:space-y-4 pt-6 md:pt-8">
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square group">
                      <div className="absolute inset-0 bg-gradient-to-br from-secondary to-primary" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Car className="w-10 h-10 text-white/80" />
                      </div>
                    </div>
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[3/4] group">
                      <div className="absolute inset-0 bg-gradient-to-br from-palm to-accent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Users className="w-12 h-12 text-white/80" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-card rounded-2xl shadow-xl p-4 md:p-6 animate-float border">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-tropical/10 flex items-center justify-center">
                      <Star className="w-6 h-6 md:w-7 md:h-7 text-tropical fill-tropical" />
                    </div>
                    <div>
                      <div className="text-2xl md:text-3xl font-bold">4.9</div>
                      <div className="text-muted-foreground text-xs md:text-sm">Average Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {featuredTestimonials.length > 0 && (
          <section className="py-20 md:py-24 bg-gradient-to-br from-primary via-primary/95 to-accent relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 border border-white rounded-full animate-pulse" />
              <div className="absolute bottom-10 right-10 w-60 h-60 border border-white rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
            </div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-10 md:mb-12">
                <Badge className="mb-4 bg-white/20 text-white border-white/30 px-4 py-1 animate-fade-in">
                  <Star className="w-4 h-4 mr-2 fill-current" />
                  Testimonials
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  What Our Travelers Say
                </h2>
                <p className="text-white/80 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  Real experiences from real travelers who chose us for their Dominican Republic adventure
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {featuredTestimonials.map((testimonial, index) => (
                  <Card 
                    key={testimonial.id}
                    className="bg-white/95 backdrop-blur-sm border-0 shadow-xl animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.15}s` }}
                  >
                    <CardContent className="p-6 md:p-8">
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 text-tropical fill-tropical" />
                        ))}
                      </div>
                      <p className="text-foreground/90 mb-6 italic text-sm md:text-base">
                        "{testimonial.comment}"
                      </p>
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-base md:text-lg font-bold text-primary">
                            {testimonial.customer_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="font-bold text-sm md:text-base">{testimonial.customer_name}</div>
                          <div className="text-xs md:text-sm text-muted-foreground">{testimonial.location}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-20 md:py-24 bg-muted/30 relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute top-20 left-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-secondary/5 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12 md:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 px-4 py-1 animate-fade-in">
                <HelpCircle className="w-4 h-4 mr-2" />
                FAQ
              </Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                Frequently Asked <span className="gradient-text">Questions</span>
              </h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Find answers to common questions about our services, booking process, and more.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm overflow-hidden">
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {faqsData.map((faq: any, index: number) => (
                      <AccordionItem 
                        key={index} 
                        value={`item-${index}`}
                        className="border-b border-border/50 last:border-0"
                      >
                        <AccordionTrigger className="px-6 py-5 text-left font-semibold hover:no-underline hover:bg-muted/50 transition-colors text-base md:text-lg group">
                          <span className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                              <MessageCircle className="w-4 h-4 text-primary" />
                            </span>
                            {faq.question}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-5 text-muted-foreground text-sm md:text-base">
                          <div className="pl-11">
                            {faq.answer}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
              
              {/* Contact CTA */}
              <div className="mt-10 text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <p className="text-muted-foreground mb-4">
                  Still have questions? We're here to help!
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/contact">
                    <Button className="bg-primary hover:bg-primary/90 shadow-lg group">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      Contact Us
                    </Button>
                  </Link>
                  <Link to="/faq">
                    <Button variant="outline" className="border-2">
                      View All FAQs
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 relative overflow-hidden">
          <div className="container mx-auto px-4">
            <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-secondary via-secondary to-tropical shadow-2xl">
              {/* Decorative patterns */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full border-4 border-white" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full border-4 border-white" />
              </div>
              
              <CardContent className="relative z-10 py-12 md:py-16 px-6 md:px-8 text-center">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 animate-fade-in">
                  Ready to Explore Paradise?
                </h2>
                <p className="text-white/90 text-base md:text-lg max-w-2xl mx-auto mb-8 md:mb-10 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  Book your transfer or tour today and experience the beauty of the Dominican Republic with our premium services
                </p>
                
                <div className="flex flex-wrap justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <Link to="/transfers">
                    <Button size="lg" className="bg-white text-secondary hover:bg-white/90 shadow-xl group">
                      Book Transfer
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <a href={`tel:${phoneNumber}`}>
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                      <Phone className="mr-2 h-5 w-5" />
                      Call Us Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
